/**
 * Template Management API
 */

import { apiFetch } from "./apiClient";
import { getKeysForType } from "../data/contentKeyRegistry";

export type TemplateStatus =
  | "onboarding"
  | "characterization_call"
  | "materials"
  | "characterization"
  | "seo_check"
  | "client_approval"
  | "design"
  | "development"
  | "site_tree"
  | "matal_handling"
  | "none"
  | "monday_add"
  | "logo"
  | "content_writing"
  | "integrations"
  | "pm_handling";
export type ProjectType = "seo" | "wp" | "wix";

export interface TemplateMeta {
  id: string;
  templateType: "branding" | "store" | "facelift";
  clientName: string;
  clientNumber: string;
  characterizer: string;
  marketer: string;
  designer: string;
  developer: string;
  /** Username of assigned content writer */
  contentWriter?: string;
  /** Username of assigned team lead */
  teamLead?: string;
  createdAt: number;
  updatedAt: number;
  /** If true, this is a master template (blueprint for creating new templates) */
  isMaster?: boolean;
  /** The master template ID this template was created from (for reference) */
  masterSourceId?: string;
  /** Custom tags for categorization */
  tags?: string[];
  /** Base64 logo URL for the template card */
  logoUrl?: string;
  /** Username of the last person who edited this template */
  lastEditedBy?: string;
  /** Workflow status: draft → review → approved → live */
  status?: TemplateStatus;
  /** Free-text field: who currently owns / is responsible for this template */
  currentOwner?: string;
  /** Base64 thumbnail URL (auto-captured preview of the site) */
  thumbnailUrl?: string;
  /** Whether the template has been archived (soft delete) */
  archived?: boolean;
  /** Timestamp when the template was archived */
  archivedAt?: number;
  /** Deadline timestamp — when this template should be completed */
  deadline?: number;
  /** Folder ID for organizational grouping */
  folderId?: string;
  /** Project type: SEO / WordPress / Wix */
  projectType?: ProjectType;
  /** White-label client branding */
  clientBranding?: ClientBranding;
}

/** Client branding configuration for white-label display */
export interface ClientBranding {
  /** Client logo URL (base64 or external) */
  logoUrl?: string;
  /** Primary brand color (hex) */
  primaryColor?: string;
  /** Secondary / accent brand color (hex) */
  accentColor?: string;
  /** Custom font family */
  fontFamily?: string;
  /** Custom footer text shown in client board */
  footerText?: string;
  /** Custom welcome message */
  welcomeMessage?: string;
  /** Whether to hide "Powered by Zap Group" attribution */
  hideAttribution?: boolean;
}

// --- Key helpers ---
const TPL_META_PREFIX = "__tpl:meta:";

function metaKey(templateId: string): string {
  return TPL_META_PREFIX + templateId;
}

// --- In-memory cache for template list ---
let _templatesCache: { data: Record<string, any>; ts: number } | null = null;
const TEMPLATES_CACHE_TTL = 30_000; // 30 seconds

async function fetchTemplatesRaw(): Promise<Record<string, any>> {
  const now = Date.now();
  if (_templatesCache && now - _templatesCache.ts < TEMPLATES_CACHE_TTL) {
    return _templatesCache.data;
  }

  // ── Consume module-level prefetch (App.tsx) if available ──
  // This avoids a duplicate /templates request: the prefetch started at
  // module load time (before React rendered), so the data is likely already
  // in-flight or resolved by now.
  const prefetch = (window as any).__ZAP_PREFETCH_TEMPLATES;
  if (prefetch) {
    delete (window as any).__ZAP_PREFETCH_TEMPLATES; // consume once
    try {
      const prefetchData = await prefetch;
      if (prefetchData && prefetchData.content) {
        const raw: Record<string, any> = prefetchData.content;
        _templatesCache = { data: raw, ts: Date.now() };
        console.log("[templateApi] Templates loaded from prefetch");
        return raw;
      }
    } catch {
      // Prefetch failed — fall through to normal fetch
      console.warn("[templateApi] Prefetch failed, falling back to normal fetch");
    }
  }

  const data = await apiFetch("/templates");
  const raw: Record<string, any> = data.content || {};
  _templatesCache = { data: raw, ts: now };
  return raw;
}

/** Invalidate the templates cache (call after create/update/delete) */
export function invalidateTemplatesCache() {
  _templatesCache = null;
}

// --- Public API ---

/** Get a single template's metadata by ID. Uses the lightweight /templates endpoint. */
export async function getTemplateMeta(templateId: string): Promise<TemplateMeta | null> {
  try {
    const raw = await fetchTemplatesRaw();
    const key = metaKey(templateId);
    const value = raw[key];
    if (value && typeof value === "object") return value as TemplateMeta;
    return null;
  } catch {
    return null;
  }
}

/** List all templates (sorted by updatedAt descending).
 *  Uses the dedicated /templates endpoint which only queries template
 *  metadata keys — avoids the Supabase 1000-row default limit issue
 *  that could truncate templates when there are many content entries.
 */
export async function listTemplates(): Promise<TemplateMeta[]> {
  const raw = await fetchTemplatesRaw();
  const metas: TemplateMeta[] = [];

  for (const [key, value] of Object.entries(raw)) {
    if (key.startsWith(TPL_META_PREFIX) && value && typeof value === "object") {
      const meta = value as TemplateMeta;
      if (meta.tags && typeof meta.tags === "string") {
        try { meta.tags = JSON.parse(meta.tags as unknown as string); } catch { meta.tags = []; }
      }
      metas.push(meta);
    }
  }

  return metas.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
}

/** Create a new template with the given metadata. */
export async function createTemplate(
  meta: Omit<TemplateMeta, "id" | "createdAt" | "updatedAt">,
): Promise<TemplateMeta> {
  const id = generateId();
  const now = Date.now();
  const template: TemplateMeta = { ...meta, id, createdAt: now, updatedAt: now };

  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [metaKey(id)]: template } }),
  });

  invalidateTemplatesCache();

  return template;
}

/** Update a template's metadata. */
export async function updateTemplate(
  id: string,
  updates: Partial<Omit<TemplateMeta, "id" | "createdAt">>,
): Promise<TemplateMeta> {
  const templates = await listTemplates();
  const existing = templates.find((t) => t.id === id);
  if (!existing) throw new Error(`Template not found: ${id}`);

  const updated: TemplateMeta = { ...existing, ...updates, updatedAt: Date.now() };

  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides: { [metaKey(id)]: updated } }),
  });

  invalidateTemplatesCache();

  return updated;
}

/** Archive a template (soft delete). */
export async function archiveTemplate(id: string): Promise<TemplateMeta> {
  return updateTemplate(id, { archived: true, archivedAt: Date.now() });
}

/** Restore a template from archive. */
export async function restoreTemplate(id: string): Promise<TemplateMeta> {
  return updateTemplate(id, { archived: false, archivedAt: undefined });
}

/** Delete a template and all its content (permanent). */
export async function deleteTemplate(id: string): Promise<void> {
  const data = await apiFetch("/content");
  const raw: Record<string, any> = data.content || {};

  const prefix = id + ":";
  const deleteOverrides: Record<string, string> = {};

  deleteOverrides[metaKey(id)] = "";

  for (const key of Object.keys(raw)) {
    if (key.startsWith(prefix)) {
      deleteOverrides[key] = "";
    }
  }

  if (Object.keys(deleteOverrides).length > 0) {
    await apiFetch("/content/bulk", {
      method: "PUT",
      body: JSON.stringify({ overrides: deleteOverrides }),
    });
  }

  invalidateTemplatesCache();
}

/** Duplicate a template. */
export async function duplicateTemplate(
  sourceId: string,
  newMeta: Omit<TemplateMeta, "id" | "createdAt" | "updatedAt">,
): Promise<TemplateMeta> {
  const newId = generateId();
  const now = Date.now();

  const template: TemplateMeta = { ...newMeta, id: newId, createdAt: now, updatedAt: now };

  const data = await apiFetch("/content");
  const raw: Record<string, any> = data.content || {};

  const sourcePrefix = sourceId + ":";
  const overrides: Record<string, any> = {};

  overrides[metaKey(newId)] = template;

  for (const [key, value] of Object.entries(raw)) {
    if (key.startsWith(sourcePrefix)) {
      const localKey = key.slice(sourcePrefix.length);
      overrides[newId + ":" + localKey] = value;
    }
  }

  await apiFetch("/content/bulk", {
    method: "PUT",
    body: JSON.stringify({ overrides }),
  });

  invalidateTemplatesCache();

  return template;
}

/**
 * Get content completion percentage for a given template.
 * For facelift type, completion is based on page statuses.
 */
export async function getTemplateCompletion(
  templateId: string,
  templateType: "branding" | "store" | "facelift",
): Promise<{ total: number; filled: number; percentage: number }> {
  if (templateType === "facelift") {
    try {
      const { getFaceliftPages, calcFaceliftCompletion } = await import("./faceliftApi");
      const pages = await getFaceliftPages(templateId);
      return calcFaceliftCompletion(pages);
    } catch (err) {
      console.error("[templateApi] getTemplateCompletion facelift error:", err);
      return { total: 0, filled: 0, percentage: 0 };
    }
  }
  try {
    const expectedKeys = getKeysForType(templateType as "branding" | "store");
    const total = expectedKeys.length;
    if (total === 0) return { total: 0, filled: 0, percentage: 0 };

    // Use template-specific endpoint instead of fetching ALL content
    const data = await apiFetch(`/content/${encodeURIComponent(templateId)}`);
    const raw: Record<string, any> = data.content || {};

    const prefix = templateId + ":";
    let filled = 0;
    for (const entry of expectedKeys) {
      const serverKey = prefix + entry.key;
      if (serverKey in raw && raw[serverKey] && typeof raw[serverKey] === "string") {
        filled++;
      }
    }

    return { total, filled, percentage: Math.round((filled / total) * 100) };
  } catch (err) {
    console.error("[templateApi] getTemplateCompletion error:", err);
    return { total: 0, filled: 0, percentage: 0 };
  }
}

/**
 * Batch get content completion for branding/store/facelift templates.
 * Each type is handled independently so a failure in one doesn't break the other.
 */
export async function getBatchCompletion(
  templates: { id: string; templateType: "branding" | "store" | "facelift" }[],
): Promise<Map<string, { total: number; filled: number; percentage: number }>> {
  const result = new Map<string, { total: number; filled: number; percentage: number }>();
  const faceliftTemplates = templates.filter((t) => t.templateType === "facelift");
  const otherTemplates = templates.filter((t) => t.templateType !== "facelift");

  // 1) Branding/store — content key based completion (independent try/catch)
  //    Uses per-template endpoint (/content/:templateId) instead of global /content
  //    to avoid fetching the entire KV store.
  if (otherTemplates.length > 0) {
    await Promise.all(
      otherTemplates.map(async (tpl) => {
        try {
          const expectedKeys = getKeysForType(tpl.templateType as "branding" | "store");
          const total = expectedKeys.length;
          if (total === 0) { result.set(tpl.id, { total: 0, filled: 0, percentage: 0 }); return; }

          const data = await apiFetch(`/content/${encodeURIComponent(tpl.id)}`);
          const raw: Record<string, any> = data.content || {};

          const prefix = tpl.id + ":";
          let filled = 0;
          for (const entry of expectedKeys) {
            const serverKey = prefix + entry.key;
            if (serverKey in raw && raw[serverKey] && typeof raw[serverKey] === "string") filled++;
          }
          result.set(tpl.id, { total, filled, percentage: Math.round((filled / total) * 100) });
        } catch (err) {
          console.error(`[templateApi] getBatchCompletion error for ${tpl.id}:`, err);
          result.set(tpl.id, { total: 0, filled: 0, percentage: 0 });
        }
      })
    );
  }

  // 2) Facelift — page-status based completion (independent try/catch)
  if (faceliftTemplates.length > 0) {
    try {
      const { getFaceliftPages, calcFaceliftCompletion } = await import("./faceliftApi");
      await Promise.all(
        faceliftTemplates.map(async (tpl) => {
          try {
            const pages = await getFaceliftPages(tpl.id);
            const comp = calcFaceliftCompletion(pages);
            result.set(tpl.id, comp);
          } catch {
            result.set(tpl.id, { total: 0, filled: 0, percentage: 0 });
          }
        })
      );
    } catch (err) {
      console.error("[templateApi] getBatchCompletion facelift error:", err);
    }
  }

  return result;
}

/** Generate a short unique ID. */
function generateId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${ts}-${rand}`;
}